howspace.components.modal = (() => {
    'use strict';

    // The element that gets focus back when a modal closes, so keyboard and screen reader users don't land on top
    // of the page.
    const returnFocusElems = new WeakMap();

    const getReturnFocusElem = elem => {
        if (elem instanceof jQuery) {
            elem = elem[0];
        }
        if (!(elem instanceof HTMLElement) || elem === document.body) {
            return null;
        }

        // A menu item is hidden once its dropdown closes, so focus returns to the dropdown toggle instead. A submenu
        // closes with its parent menu, so the toggle comes from the outermost dropdown.
        let dropdown = elem.closest('.dropdown');
        let parentDropdown = dropdown?.parentElement?.closest('.howspace-menu')?.closest('.dropdown');
        while (parentDropdown) {
            dropdown = parentDropdown;
            parentDropdown = dropdown.parentElement?.closest('.howspace-menu')?.closest('.dropdown');
        }
        const target = dropdown?.querySelector('[data-toggle="dropdown"]') ?? elem;

        // An opener inside a modal is removed with it, so the new modal inherits that modal's return target. This
        // also covers a modal opened from a MODAL_CLOSED listener while the old one is still closing.
        const openerModal = target.closest('dialog.modal');
        if (openerModal !== null) {
            return returnFocusElems.get(openerModal) ?? null;
        }

        return target;
    };

    const returnFocus = elem => {
        if (!elem?.isConnected || !$(elem).is(':visible') || elem.matches(':disabled, [aria-disabled="true"]')) {
            return;
        }
        if (document.querySelector('dialog[open]') !== null) {
            // Another modal took over, it returns focus when it closes.
            return;
        }
        elem.focus();
    };

    const modalHasEditableContent = (modal) => {
        return modal.querySelector('input:not(.howspace-form-search-input), textarea, select, .btn-success, [contenteditable]') !== null;
    };

    const closeModal = (modal) => {
        if (modal instanceof jQuery) {
            modal = modal[0];
        }

        if (!modal) {
            return;
        }

        if (modal.dataset.canCloseModal === 'false') {
            // The modal cannot be closed.
            return false;
        }

        document.body.classList.remove('modal-open');
        document.body.removeEventListener('keydown', modalKeyDown, true);
        document.body.removeEventListener('click', closeModalOnClickOutside);

        modal.querySelectorAll('.tox-tinymce-aux').forEach(aux => {
            document.body.appendChild(aux);
        });

        modal.setAttribute('closing', '');
        howspace.dispatchEvent(modal, howspace.components.modal.MODAL_CLOSING);
        modal.addEventListener('animationend', () => {
            howspace.components.destroy($(modal));
            howspace.dispatchEvent(modal, howspace.components.modal.MODAL_CLOSED);
            modal.close();
        }, {once: true});

        return true;
    };

    // Logic for this function comes from:
    // https://blog.logrocket.com/using-the-dialog-element/
    const closeModalOnClickOutside = e => {
        if (!e.isTrusted) {
            // User did not actually click, this was a "triggered" event.
            return;
        }

        const modal = e.target.closest('.modal');

        if (modal === null) {
            // User clicked an element that is not inside the modal at all.
            return;
        }

        if (e.clientY === 0 && e.clientX === 0) {
            // There are no coordinates for the click.
            return;
        }

        if (modalHasEditableContent(modal)) {
            // Modal contains some editable content.
            return;
        }

        const rect = modal.getBoundingClientRect();
        if (
            e.clientY < rect.top ||
            e.clientY > rect.bottom ||
            e.clientX < rect.left ||
            e.clientX > rect.right
        ) {
            // User clicked outside the modal.
            closeModal(modal);
        }
    };

    const modalClosed = (e) => {
        e.target.removeEventListener('close', modalClosed);
        e.target.removeEventListener('click', modalClick);
        e.target.remove();
        returnFocus(returnFocusElems.get(e.target));
    };

    const modalClick = (e) => {
        if (e.target.dataset.closeModal === 'true') {
            closeModal(e.target.closest('.modal'));
        }
    };

    const modalKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            const modal = document.querySelector('.modal[open]');
            if (modalHasEditableContent(modal)) {
                return;
            }

            const dropdown = document.querySelector('.dropdown.expanded');
            if (dropdown !== null) {
                // There's an expanded dropdown, let Escape close that and do not close the modal.
                return;
            }

            closeModal(modal);
        }
    };

    const getNewLoaderModal = () => {
        const loaderModal = document.createElement('dialog');
        loaderModal.className = 'modal small-width';
        loaderModal.innerHTML = '<div class="howspace-loader medium"></div>';
        document.body.appendChild(loaderModal);
        loaderModal.showModal();
        return loaderModal;
    };

    const checkModalElement = elem => {
        if ((elem.nodeName === 'IMG' || elem.getAttribute('role') === 'img') && elem.dataset.modal === undefined) {
            // Clicked on image, such as an avatar's initials, which doesn't have data-modal attribute. Check if the
            // parent element has it.
            elem = elem.parentElement;
        }

        if (elem.dataset.modal === undefined) {
            return;
        }

        if (elem.dataset.disabledTitle !== undefined) {
            // Element was deliberately disabled via disableWithTooltip(), do not open its modal.
            return;
        }

        let modalSettings = JSON.parse(elem.dataset.modal);
        let url = modalSettings.url;
        let fn = null;

        if (modalSettings.init) {
            fn = howspace.components.getCallback(modalSettings.init);
        }

        if (modalSettings.postData) {
            modalSettings.postDataFn = howspace.components.getCallback(modalSettings.postData);
        }

        // Delete url and init from settings, past the rest as the settings parameter.
        delete modalSettings.url;
        delete modalSettings.init;

        // Check if url has ___CURRENT_PAGE_ID___ then replace it with the current page id.
        if (url.includes('___CURRENT_PAGE_ID___')) {
            const pageId = real.dom.$body.data('page-id');
            url = url.replace('___CURRENT_PAGE_ID___', pageId);
        }

        // Check if url has ___ANALYTIC_ID___ then replace it with the current analytic id.
        if (url.includes('___ANALYTIC_ID___')) {
            const analyticId = real.dom.$body.data('analytic-id');
            url = url.replace('___ANALYTIC_ID___', analyticId);
        }

        howspace.components.modal.show(url, fn, modalSettings, elem);
    };

    // Always listen to click on body in case the click happens on an element with data-modal attribute.
    document.body.addEventListener('click', e => {
        checkModalElement(e.target);
    });

    // Always listen to keyup on body in case user hits Enter or Space while focus is in element with data-modal attribute.
    document.body.addEventListener('keyup', e => {
        if (e.key === 'Enter' || e.key === ' ') {
            checkModalElement(e.target);
        }
    });

    const getModalHtml = (urlOrElem, loaderModal, settings, modalOpener, callback) => {
        if (urlOrElem instanceof jQuery) {
            urlOrElem = urlOrElem[0];
        }
        if (urlOrElem instanceof HTMLElement) {
            urlOrElem.classList.remove('d-none');
            callback(urlOrElem.outerHTML);
            urlOrElem.classList.add('d-none');
        } else {
            let type = 'GET';
            let postData = {};
            if (typeof settings.postDataFn === 'function') {
                type = 'POST';
                postData = settings.postDataFn(modalOpener);
            }

            if (Object.keys(postData).length > 0) {
                postData = JSON.stringify(postData);
            }

            real.onTierReady(real.TIER_2, () => {
                real.ajax.html(urlOrElem, {
                    type: type,
                    data: postData,
                    success: (newModalHtml) => {
                        callback(newModalHtml);
                    },
                    error: () => {
                        // Close the loading modal, the error notification will tell what went wrong.
                        closeModal(loaderModal);
                    }
                });
            });
        }
    };

    return {
        MODAL_CLOSING: 'howspace-modal-closing',
        MODAL_CLOSED: 'howspace-modal-closed',
        MODAL_OPENED: 'howspace-modal-opened',
        show: (urlOrElem, callback = null, settings = {}, modalOpener = null) => {
            const focusFirstInput = howspace.focusFirstInput;
            let returnFocusElem = getReturnFocusElem(modalOpener ?? document.activeElement);
            const openModal = document.querySelector('.modal:not(.d-none, [closing])');
            if (openModal !== null) {
                // A modal is already open. Close it before opening a new one. The new modal returns focus to where
                // the first one would have, because the opener inside the old modal is about to be removed.
                returnFocusElem = returnFocusElems.get(openModal) ?? returnFocusElem;
                closeModal(openModal);
            }

            const loaderModal = getNewLoaderModal();
            if (returnFocusElem !== null) {
                returnFocusElems.set(loaderModal, returnFocusElem);
            }

            loaderModal.addEventListener('close', modalClosed);
            loaderModal.addEventListener('click', modalClick);
            loaderModal.dataset.settings = JSON.stringify(settings);

            document.body.classList.add('modal-open');
            document.body.addEventListener('keydown', modalKeyDown, true);
            document.body.addEventListener('click', closeModalOnClickOutside);

            getModalHtml(urlOrElem, loaderModal, settings, modalOpener, (modalHtml) => {
                // Store original width and height for animation purposes.
                let rect = loaderModal.getBoundingClientRect();
                const origHeight = rect.height;
                const origWidth = rect.width;

                // https://youmightnotneedjquery.com/#create_elements
                const template = document.createElement('template');
                template.innerHTML = modalHtml.trim();
                const newDialog = template.content.firstChild;

                // Reuse the loader modal as the new modal.
                const newModal = loaderModal;

                // Copy new dialog attributes into the new modal.
                [...newDialog.attributes].forEach(attribute => {
                    newModal.setAttribute(attribute.nodeName, attribute.nodeValue)
                });

                // Update the new modal HTML and initialize components.
                newModal.innerHTML = newDialog.innerHTML;

                const $newModal = $(newModal);
                $newModal.find('.howspace-pdf-viewer').addClass('howspace-pdf-viewer-initialized');
                $newModal.find('.howspace-picture').addClass('howspace-picture-initialized');
                howspace.components.init($newModal);

                if (focusFirstInput) {
                    howspace.form.focusFirstInput($newModal);
                }

                if (settings.fullScreen === true) {
                    newModal.classList.add('full-width', 'full-height');
                }

                // Animate the modal to new width and height.
                rect = newModal.getBoundingClientRect();
                newModal.style.height = origHeight + 'px';
                newModal.style.width = origWidth + 'px';
                newModal.style.overflow = 'hidden';
                const animProps = {
                    height: rect.height,
                    width: rect.width,
                };
                const duration = howspace.getAnimationDuration(newModal, '--modal-animation-duration');
                const completeFn = () => {
                    // Reset styles.
                    newModal.style.height = '';
                    newModal.style.width = '';
                    newModal.style.overflow = '';

                    const pausedInfiniteScroll = newModal.querySelector('.real-infinite-scroll-initialized');
                    if (pausedInfiniteScroll) {
                        // Pause + resume the infinite scroll after modal height animation is over.
                        // This will trigger loading more items in case the bottom of the list
                        // is close enough to the bottom of the modal.
                        const $pausedInfiniteScroll = $(pausedInfiniteScroll);
                        real.infiniteScroll.pause($pausedInfiniteScroll);
                        real.infiniteScroll.resume($pausedInfiniteScroll);
                    }

                    $newModal.find('.howspace-pdf-viewer').removeClass('howspace-pdf-viewer-initialized');
                    $newModal.find('.howspace-picture').removeClass('howspace-picture-initialized');
                    howspace.components.pdfViewer.init($newModal);
                    howspace.components.picture.init($newModal);

                    howspace.dispatchEvent($newModal, howspace.components.modal.MODAL_OPENED);
                };
                howspace.animate(newModal, animProps, duration, completeFn);

                if (typeof callback === 'function') {
                    callback(newModal, modalOpener);
                }
            });
        },
        close: (modal) => {
            closeModal(modal);
        },
        showUsers: (uri, title, sortBy, sortOrder, businessRule, settings = {}, callback = null) => {
            uri = encodeURIComponent(uri);
            title = encodeURIComponent(title);
            sortBy = encodeURIComponent(sortBy);
            sortOrder = encodeURIComponent(sortOrder);
            businessRule = encodeURIComponent(JSON.stringify(businessRule));

            let url = `/users/users-modal?uri=${uri}&title=${title}&sort_by=${sortBy}&sort_order=${sortOrder}&send_emails_business_rule=${businessRule}`;
            if (settings.emailType) {
                url += `&send_emails_type=${encodeURIComponent(settings.emailType)}`;
            }

            if (settings.widget) {
                url += `&send_email_from_widget=${encodeURIComponent(JSON.stringify(settings.widget))}`;
            }

            howspace.components.modal.show(url, callback);
        }
    };

})();
